/**
 * Integrytas Group - Common Security Module (CSM)
 * ErrorBoundary — wraps each tab's rendered page.
 *
 * Catches any React render/lifecycle error, prevents the crash from
 * propagating to the shell, reports the error to csm_telemetry_log,
 * and shows a user-friendly recovery UI inside the affected tab only.
 *
 * Usage (TabManagerShell wraps every renderPage() call with this):
 *   <ErrorBoundary pageCode={tab.page_internal_code} session={session}>
 *     {renderPage(tab)}
 *   </ErrorBoundary>
 */

import React from 'react';
import { reportError } from '../../lib/telemetry';
import type { AuthSession } from '../../types/auth';

interface ErrorBoundaryProps {
  children:   React.ReactNode;
  pageCode?:  string;
  session?:   AuthSession | null;
  /** Optional fallback override. */
  fallback?:  (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error:     Error | null;
  reported:  boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, reported: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (!this.state.reported) {
      void reportError(error, {
        errorType:     'FRONTEND',
        severity:      'ERROR',
        componentName: info.componentStack?.split('\n')[1]?.trim() ?? 'Unknown',
        pageCode:      this.props.pageCode,
        session:       this.props.session,
        metadata:      { componentStack: info.componentStack },
      });
      this.setState({ reported: true });
    }
  }

  reset = () => this.setState({ error: null, reported: false });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return <DefaultErrorUI error={error} pageCode={this.props.pageCode} onReset={this.reset} />;
  }
}

// ---------------------------------------------------------------------------
// Default error UI
// ---------------------------------------------------------------------------

function DefaultErrorUI({
  error, pageCode, onReset,
}: { error: Error; pageCode?: string; onReset: () => void }) {
  const [showDetail, setShowDetail] = React.useState(false);

  return (
    <div className="flex h-full flex-col items-center justify-center bg-csm-surface-alt px-8">
      <div className="w-full max-w-lg rounded border border-csm-danger-border bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                          bg-csm-danger-soft text-csm-danger text-xl font-bold">
            !
          </div>
          <div>
            <h2 className="text-csm-md font-semibold text-csm-ink">
              Something went wrong
            </h2>
            <p className="mt-0.5 text-csm-sm text-csm-muted">
              An unexpected error occurred{pageCode ? ` on ${pageCode}` : ''}. The rest
              of the application is unaffected.
            </p>
          </div>
        </div>

        {/* Error summary */}
        <div className="mb-4 rounded border border-csm-border bg-csm-surface-alt px-3 py-2
                        font-mono text-csm-xs text-csm-danger">
          {error.message}
        </div>

        {/* Detail toggle */}
        <button
          type="button"
          onClick={() => setShowDetail(s => !s)}
          className="mb-4 text-csm-xs text-csm-muted hover:text-csm-ink underline"
        >
          {showDetail ? 'Hide' : 'Show'} technical details
        </button>

        {showDetail && error.stack && (
          <pre className="mb-4 max-h-40 overflow-auto rounded border border-csm-border
                          bg-csm-surface-alt px-3 py-2 text-csm-xs text-csm-ink-soft
                          whitespace-pre-wrap break-all">
            {error.stack}
          </pre>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onReset}
            className="flex-1 h-8 rounded border border-primary bg-primary text-csm-sm
                       text-white hover:bg-primary-hover"
          >
            Try Again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-8 rounded border border-csm-border bg-white px-3 text-csm-sm
                       text-csm-ink-soft hover:bg-csm-surface-alt"
          >
            Reload Page
          </button>
        </div>

        <p className="mt-3 text-csm-2xs text-csm-muted">
          This error has been automatically reported to the system administrator.
        </p>
      </div>
    </div>
  );
}
